import { Container } from "react-grid-system";
import { useMediaQuery } from "react-responsive";

import {
  Button,
  Card,
  Link as DSLink,
  Heading,
  Icon,
  Span,
  theme,
} from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { Stack } from "components/common/stack";
import { screenSizes } from "consts";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import {
  WindDefenseContext,
  type WindDefenseContextState,
} from "pages/offer/wind-defense/wind-defense-flow";
import { trackEvent } from "utils";

import styles from "./shingle-reseal-landing.module.scss";

export const ShingleResealLanding = () => {
  const isLaptop = useMediaQuery({ maxWidth: screenSizes.laptopL });
  const isMobile = useMediaQuery({ maxWidth: screenSizes.tablet });
  const { formNavigate } =
    useMultiStepFormState<WindDefenseContextState>(WindDefenseContext);

  const quoteText =
    "My family with a roof over our heads is very important to me and SageSure recognizes this and took steps to ensure it continues.";

  return (
    <BaseLayout hideFooter>
      <Container>
        <Stack>
          <Heading size="lg">Wind Defense</Heading>
          <Card className={styles.sectionWrapperDS}>
            <Stack
              gap={isLaptop ? "xl" : "2xl"}
              className={styles.introContent}
            >
              <Heading size={isLaptop && !isMobile ? "md" : "lg"}>
                Protect your roof from hurricane force winds
              </Heading>
              <Span>
                Research shows that even newer roofs can have loose shingles,
                which can lift in strong winds and lead to tens of thousands of
                dollars in damage.
              </Span>
              <Stack gap={isLaptop ? "md" : "2xl"}>
                <Heading size="md">How do I participate?</Heading>
                <ol className={styles.steps}>
                  <li className={styles.listItem}>
                    <div className={styles.stepNumber}>
                      <Heading size="sm">1</Heading>
                    </div>
                    <div>
                      <Heading size="sm">Join the list</Heading>
                      Sign up for a chance to be selected for this free service
                    </div>
                  </li>
                  <li className={styles.listItem}>
                    <div className={styles.stepNumber}>
                      <Heading size="sm">2</Heading>
                    </div>
                    <div>
                      <Heading size="sm">Get notified if selected</Heading>
                      <Span bold className={styles.greenTextDS}>
                        Our trusted partner, LiftLock
                      </Span>
                      , will monitor the weather in your area and get in touch
                      with you to schedule a visit if selected.
                    </div>
                  </li>
                  <li className={styles.listItem}>
                    <div className={styles.stepNumber}>
                      <Heading size="sm">3</Heading>
                    </div>
                    <div>
                      <Heading size="sm">
                        Have roof prepared for strong winds
                      </Heading>
                      A trained professional will clean gutters, clear roof
                      debris, and secure loose shingles
                    </div>
                  </li>
                </ol>
              </Stack>
              <div className={styles.desktopButton}>
                <Button
                  onPress={() => {
                    trackEvent("shinglesForm_landing_JoinClicked");
                    formNavigate("policy-confirmation");
                  }}
                  data-testid="get-started-btn"
                >
                  Sign up
                </Button>
              </div>
            </Stack>
            <div
              className={styles.imageWrapper}
              role="img"
              aria-label="Roof shingles"
            ></div>
          </Card>
          <Card className={styles.wdHowDS}>
            <Stack className={styles.howWrapper}>
              <Heading size={isLaptop ? "sm" : "lg"}>
                How does Wind Defense work?
              </Heading>
              <div>
                <Span style={{ color: theme.colors.text.brand }} bold>
                  SageSure
                </Span>{" "}
                works with{" "}
                <Span style={{ color: theme.colors.text.brand }} bold>
                  LiftLock
                </Span>{" "}
                to prepare your roof to withstand high winds
              </div>
              <div className={styles.hwdWork}>
                <Card className={styles.hwdWorkItemsWrapper}>
                  <div className={styles.cleanGuttersImage}></div>
                  <div>
                    <Heading size="sm">Clean gutters</Heading>
                    Full gutters can overflow and cause water damage during a
                    storm.
                  </div>
                </Card>
                <Card className={styles.hwdWorkItemsWrapper}>
                  <div className={styles.removeRoofDebrisImage}></div>
                  <Heading size="sm">Remove roof debris</Heading>
                  Roof debris can damage roof covering and nearby homes during a
                  storm.
                </Card>
                <Card className={styles.hwdWorkItemsWrapper}>
                  <div className={styles.secureLooseShinglesImage}></div>
                  <Heading size="sm">Secure loose shingles</Heading>
                  Loose shingles can lift in strong winds causing significant
                  roof cover damage.
                </Card>
              </div>
            </Stack>
          </Card>
          <Card className={styles.quoteCardDS}>
            <div>
              <video
                autoPlay
                loop
                muted
                playsInline
                className={styles.video}
                poster="/assets/images/shingle-video-poster.jpg"
                src="/assets/videos/RoofSealing-1.mp4"
              />
              <div className={styles.overlay} />
            </div>
            <Stack className={styles.quoteStackWrapper}>
              <Heading size="lg">What customers are saying</Heading>
              <div className={styles.quoteWrapper}>
                <div>
                  <div className={styles.quoteIconWrapper}>
                    <Icon name="Talk" size="md" />
                  </div>
                </div>
                <Stack gap="lg">
                  {isMobile ? (
                    <Span color="inverse">{quoteText}</Span>
                  ) : (
                    <Heading size="msRegular">{quoteText}</Heading>
                  )}
                  <div>- Clayton J, Panama City Beach, FL</div>
                </Stack>
              </div>
            </Stack>
          </Card>
          <div className={styles.notificationDisclaimer}>
            <Span color="quiet">
              Wind Defense is not available in all areas. Selection is dependent
              on service availability and other factors. There is no guarantee
              that you will receive the service by signing up. Have more
              questions? Learn more with{" "}
              <DSLink
                href="https://sagesure.widen.net/s/khnx5fkmrq/liftlock-wind-defense-research-program-faq"
                target="_blank"
              >
                Wind Defense FAQs
              </DSLink>
            </Span>
          </div>
        </Stack>
      </Container>
      <Stack gap="md" className={styles.mobileFooter}>
        <Button
          onPress={() => {
            trackEvent("shinglesForm_landing_JoinClicked");
            formNavigate("policy-confirmation");
          }}
          fluid
        >
          Sign up
        </Button>
      </Stack>
    </BaseLayout>
  );
};
